import If from '@/storybook/components/If/If';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import cx from 'classnames';
import React, { CSSProperties, ReactElement, ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { CheckMarkIcon, CloseIcon, InfoIcon } from '../Icons/RocheIconPack';
import styles from './Chip.module.scss';

export interface ChipStyle {
  color: string;
  bgColor: string;
  borderColor: string;
}

const chipStyleDefault: ChipStyle = {
  color: '#0B41CD',
  bgColor: '#F0F4FD',
  borderColor: '#D7E0F7',
};

const chipStyleTrue: ChipStyle = {
  color: 'rgb(11, 65, 205)',
  bgColor: 'rgb(240, 244, 253)',
  borderColor: 'rgb(215, 224, 247)',
};

const chipStyleFalse: ChipStyle = {
  color: '#9D948E',
  bgColor: 'var(--grey-100)',
  borderColor: 'var(--grey-300)',
};

export const getChipStyleForBoolean = (value: boolean) => (value ? chipStyleTrue : chipStyleFalse);

interface ChipProps {
  label: string;
  children?: ReactNode;
  style?: ChipStyle & CSSProperties;
  icon?: ReactNode;
  hideInfoIcon?: boolean;
}

interface InfoPopupProps {
  title: string;
  children: ReactNode;
}

const InfoPopup = ({ title, children }: InfoPopupProps) => {
  return (
    <div>
      <h3>{title}</h3>
      <div className={styles.content}>{children}</div>
    </div>
  );
};

InfoPopup.displayName = 'InfoPopup';

const Chip = ({ label, children, style = chipStyleDefault, icon, hideInfoIcon = false }: ChipProps) => {
  const [chipRef, setChipRef] = useState<HTMLButtonElement | null>(null);
  const [panelRef, setPanelRef] = useState<HTMLElement | null>(null);
  const { styles: popperStyles, attributes } = usePopper(chipRef, panelRef, {
    modifiers: [{ name: 'offset', options: { offset: [0, 10] } }],
    placement: 'bottom',
  });
  const booleanChip = [JSON.stringify(chipStyleTrue), JSON.stringify(chipStyleFalse)].includes(JSON.stringify(style));

  const isInfoPopup = (child: ReactNode): child is ReactElement => {
    return React.isValidElement(child) && child.type === InfoPopup;
  };

  const popup = React.Children.toArray(children).find(isInfoPopup);

  const IconComponent = style === chipStyleTrue ? CheckMarkIcon : CloseIcon;

  return (
    <Popover>
      <PopoverButton
        className={cx(styles.chip, !!popup ? styles.clickable : '')}
        style={{ backgroundColor: style.bgColor, ...style }}
        ref={setChipRef}
      >
        {booleanChip && <IconComponent height="1.4rem" style={{ fill: style.color, color: style.color }} />}
        {icon}
        <div>{label}</div>
        <If condition={!!popup && !hideInfoIcon}>
          <InfoIcon height="1.6rem" style={{ fill: style.color, color: style.color }} />
        </If>
      </PopoverButton>
      <If condition={!!popup}>
        {createPortal(
          <PopoverPanel ref={setPanelRef} className={styles.popup} style={popperStyles.popper} {...attributes.popper}>
            {popup}
            <div className={styles.arrow} />
          </PopoverPanel>,
          document.body,
        )}
      </If>
    </Popover>
  );
};

Chip.InfoPopup = InfoPopup;

export default Chip;
